import React, { useEffect, useRef, useState } from 'react';
import api from './api';
import { explainReason, summaryText, formatWhen as formatReceived } from './ReceivingHistoryLog';

// Everything still open in one place:
//  1. Orders saved in 2Check but not submitted yet.
//  2. Orders verified in 2Check that Striven wouldn't accept back (customized item
//     groups, blank units inside kits, USD). The PDF, SharePoint or Drive and email
//     already happened; what's left is marking them verified in Striven by hand. Each
//     one drops off once Striven shows it as verified.
//  3. PO receipts Striven refused (a vendor that isn't in CAD, a PO that isn't approved).
//     What was scanned is kept here so it can be received in Striven by hand. Only shown
//     for orgs that have PO Receiving.

const CHECK_AT_A_TIME = 2;

const formatWhen = (iso) => {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export default function UnfinishedOrdersPage({ user, msalInstance, onOpenOrder }) {
  const [section, setSection] = useState('saved'); // 'saved' | 'mark' | 'receive'

  // To receive by hand (PO Receiving). `receipts` is null until loaded; `receiptsUnavailable` hides the tab.
  const [receipts, setReceipts] = useState(null);
  const [receiptsError, setReceiptsError] = useState('');
  const [receiptsUnavailable, setReceiptsUnavailable] = useState(false);
  const [copiedReceipt, setCopiedReceipt] = useState(null);

  // Saved, not submitted
  const [drafts, setDrafts] = useState([]);
  const [draftsLoading, setDraftsLoading] = useState(true);
  const [draftsError, setDraftsError] = useState('');
  const [resettingId, setResettingId] = useState(null);
  const [justResetId, setJustResetId] = useState(null);

  // To mark in Striven
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // id -> 'checking' | 'pending' | 'unknown'
  const [checks, setChecks] = useState({});
  const [copied, setCopied] = useState(null);
  const runId = useRef(0);

  // The backend only lets these two roles reset a draft's timer.
  const canResetTimer = user?.role === 'Admin' || user?.role === 'Supervisor';

  const getAuthHeaders = async () => {
    const authProvider = localStorage.getItem('authProvider') || 'microsoft';
    if (authProvider === 'google') {
      return { Authorization: `Bearer ${localStorage.getItem('authToken')}` };
    }
    const configRes = await api.get('/api/config/azure');
    const { clientId } = configRes.data;
    const accounts = msalInstance.getAllAccounts();
    const tokenResponse = await msalInstance.acquireTokenSilent({
      scopes: [`api://${clientId}/access_as_user`],
      account: accounts[0],
    });
    return { Authorization: `Bearer ${tokenResponse.accessToken}` };
  };

  const loadDrafts = async (headers) => {
    setDraftsLoading(true);
    setDraftsError('');
    try {
      const res = await api.get('/api/saved-orders/in-progress', { headers });
      setDrafts(res.data || []);
    } catch (err) {
      console.error('Failed to load saved orders:', err);
      setDraftsError('Could not load the saved orders.');
    } finally {
      setDraftsLoading(false);
    }
  };

  const loadToMark = async (headers) => {
    const thisRun = ++runId.current;
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/api/verifications/needs-marking', { headers });
      if (thisRun !== runId.current) return;
      const list = res.data || [];
      setRows(list);
      setChecks(Object.fromEntries(list.map(r => [r.id, 'checking'])));
      setLoading(false);
      checkStriven(list, headers, thisRun);
    } catch (err) {
      console.error('Failed to load orders to mark:', err);
      if (thisRun !== runId.current) return;
      setError('Could not load the list.');
      setLoading(false);
    }
  };

  const loadReceipts = async (headers) => {
    setReceiptsError('');
    try {
      const res = await api.get('/api/receiving/history/to-receive', { headers });
      setReceipts(res.data || []);
    } catch (err) {
      // 402: this org doesn't have PO Receiving, so there is no tab to show.
      if (err.response?.status === 402) { setReceiptsUnavailable(true); return; }
      console.error('Failed to load receipts to enter by hand:', err);
      setReceiptsError('Could not load the list.');
    }
  };

  const load = async () => {
    try {
      const headers = await getAuthHeaders();
      loadDrafts(headers);
      loadToMark(headers);
      loadReceipts(headers);
    } catch (err) {
      console.error('Could not sign in to load orders:', err);
      setDraftsError('Could not load the saved orders.');
      setError('Could not load the list.');
      setDraftsLoading(false);
      setLoading(false);
    }
  };

  // Asks Striven about each order, a couple at a time (its orders are big and slow to
  // read). An order Striven now shows as verified is removed from the list.
  const checkStriven = async (list, headers, thisRun) => {
    const queue = [...list];
    const worker = async () => {
      while (queue.length && thisRun === runId.current) {
        const row = queue.shift();
        let state = 'unknown';
        try {
          const res = await api.post('/api/verifications/striven-state', { id: row.id }, { headers });
          state = res.data?.state || 'unknown';
        } catch { /* leave as unknown */ }
        if (thisRun !== runId.current) return;
        if (state === 'marked') {
          setRows(prev => prev.filter(r => r.id !== row.id));
        } else {
          setChecks(prev => ({ ...prev, [row.id]: state }));
        }
      }
    };
    await Promise.all(Array.from({ length: CHECK_AT_A_TIME }, worker));
  };

  useEffect(() => { load(); return () => { runId.current++; }; }, []);

  const resetDraftTimer = async (orderID) => {
    setResettingId(orderID);
    try {
      const headers = await getAuthHeaders();
      const res = await api.post(`/api/admin/saved-orders/${encodeURIComponent(orderID)}/reset-timer`, null, { headers });
      // Update just this row in place instead of reloading the list.
      setDrafts(prev => prev.map(o =>
        o.orderID === orderID ? { ...o, savedAt: res.data.savedAt, expiresAt: res.data.expiresAt } : o
      ));
      setJustResetId(orderID);
      setTimeout(() => setJustResetId(id => (id === orderID ? null : id)), 1200);
    } catch (err) {
      console.error('Failed to reset draft timer:', err);
      alert(err.response?.data || 'Could not reset the timer.');
    } finally {
      setResettingId(null);
    }
  };

  // "Checked by Warehouse by X at T — file.pdf: https://link" as the same note 2Check
  // writes into Striven itself: the PDF name is a clickable link instead of a long URL.
  const noteAsHtml = (text) => {
    const esc = (v) => v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const m = text.match(/^(.*?) — (.*): (https?:\/\/\S+)$/);
    return m ? `${esc(m[1])} — <a href="${esc(m[3])}">${esc(m[2])}</a>` : esc(text);
  };

  const copyNote = async (row) => {
    try {
      // Pasting into Striven's notes keeps the link; anywhere plain-text only gets the full line.
      await navigator.clipboard.write([new ClipboardItem({
        'text/html': new Blob([noteAsHtml(row.noteText)], { type: 'text/html' }),
        'text/plain': new Blob([row.noteText], { type: 'text/plain' }),
      })]);
    } catch {
      try {
        await navigator.clipboard.writeText(row.noteText);
      } catch {
        // Older tablets block the clipboard API outside https; fall back to a hidden box.
        const box = document.createElement('textarea');
        box.value = row.noteText;
        document.body.appendChild(box);
        box.select();
        document.execCommand('copy');
        document.body.removeChild(box);
      }
    }
    setCopied(row.id);
    setTimeout(() => setCopied(c => (c === row.id ? null : c)), 2000);
  };

  const markDone = async (row) => {
    if (!window.confirm(`Take order #${row.orderId} off this list? Only do this if it's been dealt with in Striven.`)) return;
    try {
      const headers = await getAuthHeaders();
      await api.post('/api/verifications/mark-done', { id: row.id }, { headers });
      setRows(prev => prev.filter(r => r.id !== row.id));
    } catch {
      alert('Could not update the list. Try again.');
    }
  };

  const copyReceipt = async (row) => {
    const text = summaryText(row);
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Older tablets block the clipboard API outside https; fall back to a hidden box.
      const box = document.createElement('textarea');
      box.value = text;
      document.body.appendChild(box);
      box.select();
      document.execCommand('copy');
      document.body.removeChild(box);
    }
    setCopiedReceipt(row.id);
    setTimeout(() => setCopiedReceipt(c => (c === row.id ? null : c)), 2000);
  };

  const receiptDone = async (row) => {
    if (!window.confirm(`Take PO ${row.po} off this list? Only do this once it's been received in Striven.`)) return;
    try {
      const headers = await getAuthHeaders();
      await api.post('/api/receiving/history/mark-done', { id: row.id }, { headers });
      setReceipts(prev => (prev || []).filter(r => r.id !== row.id));
    } catch {
      alert('Could not update the list. Try again.');
    }
  };

  const checkLabel = (id) => ({
    checking: 'Checking Striven…',
    pending: 'Not marked in Striven yet',
    unknown: "Couldn't check Striven",
  }[checks[id]] || '');

  const busy = draftsLoading || loading;

  return (
    <div style={s.page}>
      <div style={s.titleRow}>
        <h1 style={s.title}>Unfinished orders</h1>
        <button style={s.refreshBtn} onClick={load} disabled={busy}>
          {busy ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <div style={s.tabs} role="tablist">
        <button
          role="tab"
          aria-selected={section === 'saved'}
          style={{ ...s.tab, ...(section === 'saved' ? s.tabActive : {}) }}
          onClick={() => setSection('saved')}
        >
          Saved, not submitted{!draftsLoading && !draftsError ? ` (${drafts.length})` : ''}
        </button>
        <button
          role="tab"
          aria-selected={section === 'mark'}
          style={{ ...s.tab, ...(section === 'mark' ? s.tabActive : {}) }}
          onClick={() => setSection('mark')}
        >
          To mark in Striven{!loading && !error ? ` (${rows.length})` : ''}
        </button>
        {!receiptsUnavailable && (
          <button
            role="tab"
            aria-selected={section === 'receive'}
            style={{ ...s.tab, ...(section === 'receive' ? s.tabActive : {}) }}
            onClick={() => setSection('receive')}
          >
            To receive by hand{receipts && !receiptsError ? ` (${receipts.length})` : ''}
          </button>
        )}
      </div>

      {/* ── Saved, not submitted ─────────────────────────────── */}
      {section === 'saved' && (
      <section style={s.section}>
        <p style={s.sub}>
          Orders someone saved part way through verifying. Tap an order number to pick it back up.
          A saved order is deleted after its timer runs out.
        </p>

        {draftsError && <p style={s.error}>{draftsError}</p>}
        {!draftsError && !draftsLoading && drafts.length === 0 && <p style={s.empty}>No saved orders.</p>}

        <div style={s.list}>
          {drafts.map(o => {
            const daysLeft = Math.ceil((new Date(o.expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
            const expiryLabel = daysLeft <= 0 ? 'expires today' : daysLeft === 1 ? 'expires tomorrow' : `expires in ${daysLeft}d`;
            const isResetting = resettingId === o.orderID;
            return (
              <div
                key={o.orderID}
                style={{
                  ...s.draftRow,
                  backgroundColor: justResetId === o.orderID ? '#e9fbf1' : '#fff',
                  transition: 'background-color 1.1s ease',
                }}
              >
                {onOpenOrder ? (
                  <button type="button" style={s.draftOpenBtn} onClick={() => onOpenOrder(o.orderID)} title="Open this saved order">
                    Order #{o.orderID}
                  </button>
                ) : (
                  <span style={s.orderTag}>Order #{o.orderID}</span>
                )}
                <span style={s.draftDate}>
                  Saved {new Date(o.savedAt).toLocaleDateString()} at {new Date(o.savedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  {' · '}<span style={daysLeft <= 2 ? s.expirySoon : s.expiry}>{expiryLabel}</span>
                  {canResetTimer && (
                    <button
                      type="button"
                      style={{ ...s.resetBtn, opacity: isResetting ? 0.6 : 1 }}
                      disabled={isResetting}
                      onClick={() => resetDraftTimer(o.orderID)}
                    >
                      {isResetting ? 'Resetting…' : 'Reset timer'}
                    </button>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </section>
      )}

      {/* ── To mark in Striven ───────────────────────────────── */}
      {section === 'mark' && (
      <section style={s.section}>
        <p style={s.sub}>
          These are verified in 2Check, and the PDF has been saved and sent, but Striven wouldn't accept them,
          so they still need marking verified in Striven by hand. Paste the note into the order's internal notes.
          An order drops off this list once Striven shows it as verified.
        </p>

        {error && <p style={s.error}>{error}</p>}
        {!error && !loading && rows.length === 0 && <p style={s.empty}>Nothing to mark. All caught up.</p>}

        <div style={s.list}>
          {rows.map(row => (
            <div key={row.id} style={s.row}>
              <div style={s.rowHead}>
                <span style={s.orderTag}>Order #{row.orderId}</span>
                {row.orderName && <span style={s.orderName}>{row.orderName}</span>}
                <span style={s.reason}>{row.reason}</span>
                <span style={s.time}>{row.level === 'level2' ? 'Level 2' : 'Level 1'} · {row.verifiedBy} · {formatWhen(row.completedAt)}</span>
              </div>
              {row.noteText && <div style={s.note}>{row.noteText}</div>}
              <div style={s.actions}>
                <span style={checks[row.id] === 'pending' ? s.statusPending : s.status}>{checkLabel(row.id)}</span>
                <div style={s.buttons}>
                  {row.pdfUrl && <a href={row.pdfUrl} target="_blank" rel="noreferrer" style={s.link}>Open PDF</a>}
                  {row.noteText && (
                    <button type="button" style={s.copyBtn} onClick={() => copyNote(row)}>
                      {copied === row.id ? 'Copied ✓' : 'Copy note'}
                    </button>
                  )}
                  <button type="button" style={s.doneBtn} onClick={() => markDone(row)}>Done</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
      )}

      {/* ── To receive by hand (PO Receiving) ────────────────── */}
      {section === 'receive' && !receiptsUnavailable && (
      <section style={s.section}>
        <p style={s.sub}>
          These POs were scanned in 2Check, but Striven refused the update (for example the vendor isn't in CAD, or the
          PO isn't approved), so nothing was received in Striven. Copy the summary and receive them in Striven by hand.
        </p>

        {receiptsError && <p style={s.error}>{receiptsError}</p>}
        {!receiptsError && receipts && receipts.length === 0 && <p style={s.empty}>Nothing to receive by hand.</p>}

        <div style={s.list}>
          {(receipts || []).map(row => (
            <div key={row.id} style={s.row}>
              <div style={s.rowHead}>
                <span style={s.orderTag}>PO {row.po}</span>
                <span style={s.reason}>Not in Striven</span>
                <span style={s.time}>{row.by} · {formatReceived(row.at)}</span>
              </div>
              <div style={s.status}>{explainReason(row.reason)}</div>
              <div style={s.note}>{summaryText(row)}</div>
              <div style={s.actions}>
                <span style={s.status}>{row.totalUnits} units</span>
                <div style={s.buttons}>
                  <button type="button" style={s.copyBtn} onClick={() => copyReceipt(row)}>
                    {copiedReceipt === row.id ? 'Copied ✓' : 'Copy summary'}
                  </button>
                  <button type="button" style={s.doneBtn} onClick={() => receiptDone(row)}>Done</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
      )}
    </div>
  );
}

const s = {
  page: { padding: '32px 24px', maxWidth: 900, margin: '0 auto' },
  titleRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 },
  title: { fontSize: 24, fontWeight: 700, margin: 0 },
  section: { marginBottom: 36 },
  tabs: { display: 'flex', gap: 6, marginBottom: 14, borderBottom: '1px solid #eee', flexWrap: 'wrap' },
  tab: { padding: '8px 14px', background: 'none', border: 'none', borderBottom: '2px solid transparent', cursor: 'pointer', fontSize: 14, color: '#666', marginBottom: -1 },
  tabActive: { color: '#111', fontWeight: 600, borderBottom: '2px solid rgb(65, 159, 196)' },
  sub: { fontSize: 13, color: '#888', marginBottom: 16, marginTop: 0, lineHeight: 1.5 },
  refreshBtn: { padding: '6px 16px', background: 'rgb(65, 159, 196)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 },
  error: { color: '#b91c1c', fontSize: 14 },
  empty: { color: '#aaa', fontSize: 14 },
  list: { display: 'flex', flexDirection: 'column', gap: 10 },
  draftRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '10px 14px', border: '1px solid #eee', borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
  draftOpenBtn: { fontWeight: 600, fontSize: 14, color: 'rgb(43, 106, 131)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3 },
  draftDate: { fontSize: 12, color: '#888' },
  expiry: { fontSize: 12, color: '#888' },
  expirySoon: { fontSize: 12, color: '#c2410c', fontWeight: 600 },
  resetBtn: { marginLeft: 10, padding: '3px 10px', fontSize: 12, fontWeight: 600, color: '#555', background: '#fff', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer' },
  row: { background: '#fff', border: '1px solid #eee', borderRadius: 10, padding: '14px 16px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column', gap: 10 },
  rowHead: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  orderTag: { fontSize: 14, fontWeight: 700, color: '#333' },
  orderName: { fontSize: 13, color: '#555' },
  reason: { fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 100, background: '#fff7ed', color: '#c2410c', textTransform: 'uppercase', letterSpacing: 0.3 },
  time: { fontSize: 12, color: '#999', marginLeft: 'auto' },
  note: { fontSize: 12, color: '#444', fontFamily: 'ui-monospace, monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: '#fafafa', border: '1px solid #eee', borderRadius: 6, padding: 10 },
  actions: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' },
  status: { fontSize: 12, color: '#999' },
  statusPending: { fontSize: 12, color: '#c2410c', fontWeight: 600 },
  buttons: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  link: { fontSize: 13, color: 'rgb(65, 159, 196)', textDecoration: 'none', padding: '6px 4px' },
  copyBtn: { padding: '6px 14px', background: 'rgb(65, 159, 196)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 },
  doneBtn: { padding: '6px 14px', background: '#fff', color: '#555', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer', fontSize: 13 },
};
